import axios from 'axios';
import { redactUrl } from './url-redaction';

/**
 * Derives HTTP attributes from an axios error.
 *
 * Pure by design: it is called from `logError`, NOT from the response
 * interceptor in `api.instance.ts`. That interceptor fires on every rejected
 * response, including ones the app handles deliberately (e.g. the
 * `/session/last-booking` 404 that legitimately means "no booking yet"), so
 * reporting from there would turn routine failures into errors.
 *
 * Note the interceptor rebuilds errors via `new AxiosError(msg, code,
 * error.config, error.request, response)`, so `config` and `response` survive
 * and this still works on the reconstructed error.
 *
 * Identifier masking lives in `redactUrl`, so request URLs and `page.url` are
 * normalised by the same rules rather than drifting apart.
 *
 * Returns `{}` for anything that is not an axios error.
 */
export const enrichAxiosError = (error: unknown): Record<string, unknown> => {
  if (!axios.isAxiosError(error)) return {};

  const { config, response } = error;

  return {
    'http.url': redactUrl(config?.url) || undefined,
    'http.method': config?.method?.toUpperCase(),
    'http.status_code': String(response?.status ?? 'unknown'),
  };
};
